import React, { ReactElement } from 'react';
import classnames from 'classnames';
import { SortOrder } from '../../../api/shared.dto';
import './tables.scss';
import ExpandArrowSVG from '../../../images/svg/icons8-expand-arrow';
import Text from '../text/text';
import { Permission } from '../../../api/roles.dao';

// todo: array of THeadCell doesn't include information about the width,
//  but it is required to ensure UI is stable while re-loading the data. Add this capability.
// todo: ideally, sorting shall not be embedded into the ListLayout
//  instead there should be a possibility to insert custom nodes into particular header cells
//  similarly to how react nodes can be inserted in current non-sortable headers
type THeadCell = string | React.ReactNode;

interface ITheadSort<TSortKeys> {
    title?: string | ReactElement;
    sortField?: TSortKeys;
    defaultSortOrder?: SortOrder;
    customNode?: React.ReactNode;
    width?: string | number;
    id?: string;
    byDefault?: boolean;
    alternativeTitle?: string;
    columnPermission?: Permission;
    className?: string;
}

interface ISortOptions<TSortKeys> {
    theads: ITheadSort<TSortKeys>[];
    sortRows?: (field: TSortKeys, direction: SortOrder) => void;
    sortField?: TSortKeys;
    sortOrder?: SortOrder;
}

interface IStaticHeaderCellProps {
    cellContent: THeadCell;
    width?: string | number;
    className?: string;
}

const CellHeaderButton = ({ cellContent, className = '' }) =>
    <div className='cell-header-button'>
        <Text message={cellContent} withEllipsis className={className}/>
    </div>;

export const THeader = (props: IStaticHeaderCellProps) => {
    const { cellContent, width, className = '' } = props;
    const widthStyle = width ? {width: width} : null;

    return (
        <th {...widthStyle}>
            {(typeof cellContent === 'string' && cellContent) ?
                <CellHeaderButton cellContent={cellContent} className={className}/> : cellContent
            }
        </th>
    );
};

export const THeaderSort = <T, >(props: ISortOptions<T>) => {
    return <>
        {props.theads.map((item, index) => {
            if (!item) return null;
            // if sorting by field is available - ignore customNode prop as it does not stack with sorting
            if (item.sortField) {
                const isSelected = props.sortField?.toString() === item.sortField?.toString();
                const sortHeaderClasses = classnames('cell-header-button cell-header-button--sortable', {
                   'cell-header-button--active': isSelected,
                   'cell-header-button--with-asc-sort': isSelected && (props.sortOrder === SortOrder.Asc),
                   'cell-header-button--with-desc-sort': isSelected && (props.sortOrder === SortOrder.Desc),
                });

                // shall set 'desc' sorting for previously unsorted column if default sort order for this column is not set explicitely
                // otherwise - revert sort order
                let newSortOrder = item.defaultSortOrder || SortOrder.Asc;
                if (isSelected) {
                    newSortOrder = props.sortOrder === SortOrder.Desc ? SortOrder.Asc : SortOrder.Desc;
                }

                const widthStyle = item.width ? {width: item.width} : null;

                return (
                    <th key={index} {...widthStyle}>
                        <div className={sortHeaderClasses}
                              onClick={() => props.sortRows(item.sortField, newSortOrder)}
                        >
                            <Text message={item.title as string} withEllipsis className={item.className}/>
                            {<ExpandArrowSVG />}
                        </div>
                    </th>
                );
            } else {
                // if sorting by this particular field is not expected - pick either title or custom node to be displayed
                const cellContent = item.customNode || item.title;

                // typescript but which prevents from union types in JSX when one of the unified types is reactNode
                // @ts-ignore
                return <THeader cellContent={cellContent} key={index} width={item.width} className={item.className} />;
            }
        })}
    </>;
};

const ListLayout = <T, >(props: React.PropsWithChildren<{
    thead?: THeadCell[];
    // sortable headers are built-in the ListLayout and to be provided as sortOptions argument
    sortOptions?: ISortOptions<T>;
    isDisabled?: boolean;
}>) => {
    return (
        <table className='table-list-layout w-100' cellPadding={0} cellSpacing={0}>
            <thead className={classnames('table-list-layout__thead', {'disabled-thead': props.isDisabled})}>
                <tr>
                    {props.sortOptions ? <THeaderSort {...props.sortOptions}/> : (
                        <>
                            {props.thead?.map((item, index) => <THeader cellContent={item} key={index} />)}
                        </>)
                    }
                </tr>
            </thead>
            <tbody className='table-list-layout__tbody'>
                {props.children}
            </tbody>
        </table>
    );
};

export { ListLayout, CellHeaderButton, ISortOptions, ITheadSort };